import type { PermissionAction } from '@broker/contracts';
import { useApplications, useChannels, usePermissions, useSetPermissions } from '../api/queries.js';
import { Card, Loading, Page, Problem } from '../components/ui.jsx';

/**
 * The matrix of who may publish and who may consume, on every channel.
 *
 * It is written as an end state, not as a list of changes: ticking a box sends the full set of
 * actions for that pair, so what you see is exactly what Broker will enforce on the next call —
 * including for tokens that were issued before the change.
 */
export function Permissions() {
  const applications = useApplications();
  const channels = useChannels();
  const permissions = usePermissions();
  const save = useSetPermissions();

  if (applications.isLoading || channels.isLoading || permissions.isLoading) return <Loading what="i permessi" />;
  const error = applications.error ?? channels.error ?? permissions.error;
  if (error) return <Problem error={error} />;

  const granted = new Set(permissions.data!.permissions.map((permission) => `${permission.application}|${permission.channel}|${permission.action}`));
  const has = (application: string, channel: string, action: PermissionAction) => granted.has(`${application}|${channel}|${action}`);

  const toggle = (application: string, channel: string, action: PermissionAction) => {
    const actions: PermissionAction[] = (['publish', 'consume'] as const).filter((candidate) =>
      candidate === action ? !has(application, channel, action) : has(application, channel, candidate),
    );
    void save.mutateAsync({ slug: application, channel, actions });
  };

  const live = channels.data!.channels.filter((channel) => !channel.archived);

  return (
    <Page eyebrow="CONFIGURA" title="Permissions" description="Chi può pubblicare e chi può consumare, canale per canale.">
      {save.error ? <Problem error={save.error} /> : null}
      <Card hint="P = publish · C = consume">
        <div className="table-wrap">
          <table className="matrix">
            <thead>
              <tr>
                <th>Applicazione</th>
                {live.map((channel) => (
                  <th key={channel.id} className="vertical" title={channel.name}>
                    <span>{channel.name}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {applications.data!.applications.map((application) => (
                <tr key={application.id}>
                  <td>
                    <strong>{application.slug}</strong>
                  </td>
                  {live.map((channel) => (
                    <td key={channel.id} className="matrix-cell">
                      {(['publish', 'consume'] as const).map((action) => (
                        <button
                          key={action}
                          type="button"
                          className={`chip${has(application.slug, channel.name, action) ? ' on' : ''}`}
                          title={`${application.slug} · ${action} · ${channel.name}`}
                          disabled={save.isPending}
                          onClick={() => toggle(application.slug, channel.name, action)}
                        >
                          {action === 'publish' ? 'P' : 'C'}
                        </button>
                      ))}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {live.length === 0 ? <p className="empty muted">Nessun canale attivo da autorizzare.</p> : null}
      </Card>
    </Page>
  );
}
